<template>
  <div
    class="schematic-canvas"
    :class="{ 'animations-paused': animationsPaused }"
    ref="canvasWrap"
  >
    <!-- Pipe length warning -->
    <div
      v-if="totalPipeLength_ft > 0 && pipeLengthStatus !== 'ok'"
      class="d-flex align-items-center gap-2 px-3 py-2 small fw-bold"
      :class="pipeLengthStatus === 'short' ? 'text-warning' : 'text-danger'"
      role="status"
    >
      <i
        class="bi"
        :class="
          pipeLengthStatus === 'short'
            ? 'bi-arrow-down-circle-fill'
            : 'bi-arrow-up-circle-fill'
        "
        aria-hidden="true"
      ></i>
      <span v-if="pipeLengthStatus === 'short'">
        Pipe too short &mdash; {{ totalPipeLength_ft.toFixed(1) }}&nbsp;ft
        placed, need {{ targetPipeLength_ft.toFixed(1) }}&nbsp;ft
      </span>
      <span v-else>
        Pipe too long &mdash; {{ totalPipeLength_ft.toFixed(1) }}&nbsp;ft
        placed, target is {{ targetPipeLength_ft.toFixed(1) }}&nbsp;ft
      </span>
    </div>

    <!-- Missing component warning -->
    <div
      v-if="missingSlots.length > 0"
      class="d-none d-flex align-items-center gap-2 px-3 py-2 small fw-semibold text-danger"
    >
      <i class="bi bi-exclamation-circle-fill" aria-hidden="true"></i>
      <span>
        {{ missingSlots.length }} slot{{ missingSlots.length !== 1 ? "s" : "" }}
        still empty:
        <template v-for="(slot, index) in missingSlots" :key="slot.label">
          {{ slot.label }}{{ index < missingSlots.length - 1 ? ", " : "" }}
        </template>
      </span>
    </div>

    <PumpingDesignSchematic
      v-bind="{ PIPE_Y, SRC_X, TANK_W, TANK_H, FLUID_LINE_H, DEST_Y }"
      :animations-paused="animationsPaused"
      :view-box="viewBox"
      :pipe-segments="pipeSegments"
      :rise-start-x="riseStartX"
      :rise-end-x="riseEndX"
      :rise-end-y="riseEndY"
      :last-slot-right="lastSlotRight"
      :diagonal-layout="diagonalLayout"
      :elev-px="elevPx"
      :elevation-change_ft="elevationChange_ft"
      :src-y="srcY"
      :fluid-lines="fluidLines"
      :source-level_ft="sourceLevel_ft"
      :top-pipe-segments="topPipeSegments"
      :top-layout="topLayout"
      :slot-assignments="slotAssignments"
      :active-slot="activeSlot"
      :dest-tank-x="destTankX"
      :slot-layout="slotLayout"
      :last-assigned-slot="lastAssignedSlot"
      @select-slot="$emit('select-slot', $event)"
      @clear-slot="$emit('clear-slot', $event)"
      @clear-last-assigned="$emit('clear-last-assigned')"
    />
  </div>
</template>

<script>
import { M_TO_FT } from "@/utils/units.js";
import PumpingDesignSchematic from "@/components/activity/PumpingDesignSchematic.vue";

const TANK_W = 80;
const TANK_H = 130;
const PIPE_Y = 260; // y of horizontal pipe centerline
const GAP = 24; // gap between nodes
const SRC_X = 20;
// SRC_Y is now a computed (srcY) that accounts for sourceLevel_ft

// Half-widths and half-heights for each slot type
const SLOT_HW = { pipe: 32, pump: 42, valve: 44, fitting: 22 };
const SLOT_HH = { pipe: 14, pump: 42, valve: 66, fitting: 16 };
const SLOT_HW_DEFAULT = 30; // fallback hw for unknown slot accepts
const SLOT_HH_DEFAULT = 20; // fallback hh for unknown slot accepts
const FLUID_LINE_H = 13; // px between fluid-name tspan lines inside tanks

// Unit conversion
const M_TO_PX = 3.2; // metres → SVG px (elevation scale)
const FT_TO_PX = M_TO_PX / M_TO_FT; // feet → SVG px (consistent scale)
const MAX_ELEV_PX = 150; // cap on visual elevation rise
const DEFAULT_SOURCE_LEVEL_FT = 10; // default source water level in feet
const FLUID_NAME_SINGLE_LINE_MAX_CHARS = 12;
const PIPE_LENGTH_TOLERANCE_FT = 1;

// Diagonal layout
const DIAG_SLOT_PX = 60; // extra px per diagonal slot along the pipe
const MIN_DIAG_PX = 120; // minimum diagonal length in px

// Canvas padding
const CANVAS_PAD_L = 60; // left-side padding before source tank
const CANVAS_PAD_R = 16; // right-side padding after dest tank
const VIEWBOX_PAD_T = 30; // viewBox top margin above dest tank
const VIEWBOX_PAD_B = 80; // viewBox bottom margin below pipe level

export default {
  name: "PumpingDesignCanvas",
  components: {
    PumpingDesignSchematic
  },
  props: {
    scenario: { type: Object, required: true },
    slots: { type: Array, required: true },
    slotAssignments: { type: Object, default: () => ({}) },
    activeSlot: { type: String, default: null },
    simResult: { type: Object, default: null },
    lastAssignedSlot: { type: String, default: null },
    animationsPaused: { type: Boolean, default: false }
  },
  emits: ["select-slot", "clear-slot", "clear-last-assigned"],
  data() {
    return { PIPE_Y, TANK_W, TANK_H, SRC_X, FLUID_LINE_H };
  },
  computed: {
    sourceLevel_ft() {
      return this.scenario.sourceLevel_ft ?? DEFAULT_SOURCE_LEVEL_FT;
    },
    sourceLevelPx() {
      return Math.round(Math.min(this.sourceLevel_ft * FT_TO_PX, MAX_ELEV_PX));
    },
    srcY() {
      return PIPE_Y - TANK_H - this.sourceLevelPx;
    },
    fluidLines() {
      // Split fluid name into max two lines that fit within the tank width
      const name = this.scenario.fluidType || "";
      const mid = Math.ceil(name.length / 2);
      const space = name.lastIndexOf(" ", mid);
      if (name.length <= FLUID_NAME_SINGLE_LINE_MAX_CHARS || space === -1)
        return [name, null];
      return [name.slice(0, space), name.slice(space + 1)];
    },
    elevationChange_ft() {
      return (this.scenario.elevationChange_m * M_TO_FT).toFixed(1);
    },
    elevPx() {
      return Math.round(
        Math.min(this.scenario.elevationChange_m * M_TO_PX, MAX_ELEV_PX)
      );
    },
    srcRight() {
      return SRC_X + TANK_W;
    },
    slotLayout() {
      let x = this.srcRight + GAP;
      return this.slots
        .filter((s) => s.zone === "horizontal")
        .map((slot) => {
          const hw = SLOT_HW[slot.accepts] || SLOT_HW_DEFAULT;
          const hh = SLOT_HH[slot.accepts] || SLOT_HH_DEFAULT;
          const cx = x + hw;
          x = cx + hw + GAP;
          return { ...slot, cx, hw, hh };
        });
    },
    diagonalSlots() {
      return this.slots.filter((s) => s.zone === "diagonal");
    },
    topSlots() {
      return this.slots.filter((s) => s.zone === "top");
    },
    lastSlotRight() {
      if (!this.slotLayout.length) return this.srcRight;
      const last = this.slotLayout[this.slotLayout.length - 1];
      return last.cx + last.hw;
    },
    riseX() {
      return this.lastSlotRight + GAP;
    },
    riseStartX() {
      return this.lastSlotRight + GAP;
    },
    diagonalLength() {
      // Base length from elevation, plus space for diagonal-only slots
      const slotSpace = this.diagonalSlots.length * DIAG_SLOT_PX;
      return Math.max(this.elevPx, MIN_DIAG_PX) + slotSpace;
    },
    riseEndX() {
      // 45° means dx = dy
      return this.riseStartX + this.diagonalLength;
    },
    riseEndY() {
      return PIPE_Y - this.diagonalLength;
    },
    diagonalLayout() {
      // Distribute diagonal slots evenly along the diagonal
      const count = this.diagonalSlots.length;
      if (!count) return [];
      const totalLen = this.diagonalLength;
      const spacing = totalLen / (count + 1);
      return this.diagonalSlots.map((slot, i) => {
        const t = (spacing * (i + 1)) / totalLen; // fraction along diagonal
        const cx = this.riseStartX + t * (this.riseEndX - this.riseStartX);
        const cy = PIPE_Y + t * (this.riseEndY - PIPE_Y);
        const hw = SLOT_HW[slot.accepts] || SLOT_HW_DEFAULT;
        const hh = SLOT_HH[slot.accepts] || SLOT_HH_DEFAULT;
        return { ...slot, cx, cy, hw, hh };
      });
    },
    topLayout() {
      // Lay out top-zone slots horizontally at the elevation level
      let x = this.riseEndX + GAP;
      return this.topSlots.map((slot) => {
        const hw = SLOT_HW[slot.accepts] || SLOT_HW_DEFAULT;
        const hh = SLOT_HH[slot.accepts] || SLOT_HH_DEFAULT;
        const cx = x + hw;
        x = cx + hw + GAP;
        return { ...slot, cx, hw, hh };
      });
    },
    topRight() {
      if (!this.topLayout.length) return this.riseEndX;
      const last = this.topLayout[this.topLayout.length - 1];
      return last.cx + last.hw;
    },
    topPipeSegments() {
      // Horizontal pipe segments at the top level connecting diagonal end → fittings → dest tank
      const segs = [];
      const nodes = [
        { id: "rise-end", left: this.riseEndX - 4, right: this.riseEndX },
        ...this.topLayout.map((s) => ({
          id: s.id,
          left: s.cx - s.hw,
          right: s.cx + s.hw
        })),
        { id: "dest", left: this.destTankX, right: this.destTankX + TANK_W }
      ];
      nodes.slice(0, -1).forEach((node, i) => {
        const x1 = node.right;
        const x2 = nodes[i + 1].left;
        if (x2 > x1) segs.push({ id: `top-seg-${i}`, x1, x2 });
      });
      return segs;
    },
    destTankX() {
      return this.topRight + GAP;
    },
    DEST_Y() {
      return this.riseEndY - TANK_H / 2;
    },
    pipeSegments() {
      const pumpIdx = this.slotLayout.findIndex((s) => s.accepts === "pump");
      const segs = [];
      const nodes = [
        { id: "src", left: SRC_X, right: SRC_X + TANK_W / 2 },
        ...this.slotLayout.map((s) => ({
          id: s.id,
          left: s.cx - s.hw,
          right: s.cx + s.hw
        }))
      ];
      nodes.slice(0, -1).forEach((node, i) => {
        const x1 = node.right;
        const x2 = nodes[i + 1].left;
        // Suction side: segments up to and including the gap entering the pump
        const suction = pumpIdx === -1 || i <= pumpIdx;
        if (x2 > x1) segs.push({ id: `seg-${i}`, x1, x2, suction });
      });
      return segs;
    },
    viewBox() {
      const left = SRC_X - CANVAS_PAD_L;
      const right = this.destTankX + TANK_W + CANVAS_PAD_R;
      const top = Math.min(this.DEST_Y, this.srcY) - VIEWBOX_PAD_T;
      const bottom = PIPE_Y + TANK_H / 2 + VIEWBOX_PAD_B;
      return `${left} ${top} ${right - left} ${bottom - top}`;
    },
    totalPipeLength_ft() {
      return this.slots
        .filter((s) => s.accepts === "pipe" && !!this.slotAssignments[s.id])
        .reduce(
          (sum, s) => sum + (this.slotAssignments[s.id].length_ft || 0),
          0
        );
    },
    targetPipeLength_ft() {
      return this.scenario.pipeRunLength_m * M_TO_FT;
    },
    pipeLengthStatus() {
      if (this.totalPipeLength_ft === 0) return "ok";
      const diff = this.totalPipeLength_ft - this.targetPipeLength_ft;
      if (diff < -PIPE_LENGTH_TOLERANCE_FT) return "short";
      if (diff > PIPE_LENGTH_TOLERANCE_FT) return "long";
      return "ok";
    },
    missingSlots() {
      return this.slots.filter((s) => !this.slotAssignments[s.id]);
    }
  }
};
</script>

<style scoped>
.schematic-canvas {
  flex: 1;
  width: 100%;
  min-height: 340px;
  max-width: none;
  max-height: none;
  aspect-ratio: auto;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.schematic-svg {
  width: 100%;
  height: 100%;
  display: block;
}
@keyframes flowRight {
  to {
    stroke-dashoffset: -56;
  }
}
@keyframes flowUp {
  to {
    stroke-dashoffset: -56;
  }
}
.flow-dash {
  animation: flowRight 1.4s linear infinite;
}
.flow-dash--up {
  animation: flowUp 1.4s linear infinite;
}
@keyframes slotPopFlash {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
.slot-pop-flash {
  animation: slotPopFlash 0.65s ease-out forwards;
}

.animations-paused .flow-dash,
.animations-paused .flow-dash--up,
.animations-paused .slot-pop-flash {
  animation-play-state: paused;
}
</style>
